import React from 'react';
import { motion } from 'framer-motion';
import { Award, Shield, Heart, Gem } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-serif text-5xl md:text-6xl mb-6">About The Gem Atelier</h1>
            <p className="text-xl text-gray-700 leading-relaxed text-center">
              <em>"At The Gem Atelier, we believe that a gemstone is more than a beautiful object—it is a fragment of Earth's history, shaped over millions of years, waiting for the right person to continue its story."</em>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <p className="text-2xl font-light text-gray-600 mb-4">Our atelier was founded with a singular mission:</p>
            <h2 className="font-serif text-4xl md:text-5xl mb-8">
              <em>"To curate gemstones of exceptional beauty, integrity, and soul."</em>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center"
          >
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Each stone in our collection is selected by hand, not just for its color or clarity, but for its presence—for that unmistakable feeling when a gem speaks to you. From vivid Zambian emeralds to royal Burmese rubies and velvety Sri Lankan sapphires, we choose creations that embody rarity, elegance, and emotional resonance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Craftsmanship Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl mb-8 text-center">Where Craftsmanship Meets Curation</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Unlike mass-market jewelers, we do not deal in volume. We operate as a boutique gem house, curating a limited selection that meets the highest standards of:
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="flex items-start space-x-4">
                <Shield className="w-8 h-8 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-medium text-xl mb-2">Authenticity</h3>
                  <p className="text-gray-600">Every stone is tested and verifiable</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Gem className="w-8 h-8 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-medium text-xl mb-2">Natural Beauty</h3>
                  <p className="text-gray-600">Unheated and untreated stones when possible</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Award className="w-8 h-8 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-medium text-xl mb-2">Origin Significance</h3>
                  <p className="text-gray-600">Traceable provenance and heritage</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Heart className="w-8 h-8 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-medium text-xl mb-2">Investment Potential</h3>
                  <p className="text-gray-600">Appreciating assets for generations</p>
                </div>
              </div>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed italic text-center">
              Every gemstone is examined through a collector's eye, a jeweler's precision, and a storyteller's heart.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 px-4 bg-black text-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl mb-8 text-center">Our Philosophy: Slow Luxury</h2>
            
            <p className="text-xl text-gray-300 mb-8 text-center">
              In a world driven by speed, we embrace the opposite.
            </p>

            <div className="space-y-4 mb-12">
              <p className="text-lg text-gray-300">We believe in:</p>
              <ul className="space-y-3 text-lg text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-white rounded-full mr-4"></span>
                  Thoughtful sourcing
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-white rounded-full mr-4"></span>
                  Ethical partnerships
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-white rounded-full mr-4"></span>
                  Transparency in origin and treatment
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-white rounded-full mr-4"></span>
                  Bespoke service
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-white rounded-full mr-4"></span>
                  Only offering gems we would proudly own ourselves
                </li>
              </ul>
            </div>

            <p className="text-lg text-gray-300 text-center italic">
              By limiting our inventory, we maintain the integrity and exclusivity that luxury deserves.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Purpose Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl mb-8 text-center">Why We Exist</h2>
            
            <p className="text-xl text-gray-700 leading-relaxed mb-6 text-center">
              We exist for the individual who sees beauty not merely as decoration, but as heritage—
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Someone who understands that a gemstone is a legacy piece that transcends fashion.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed">
              Whether you are a first-time buyer or a seasoned collector, our goal is to help you discover a gemstone that becomes a part of your story—and one that will be cherished for generations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Closing Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl md:text-5xl mb-8">Curators of Exceptional Gems</h2>
            
            <div className="space-y-4 text-xl text-gray-700">
              <p>Welcome to The Gem Atelier.</p>
              <p>Where rarity is preserved.</p>
              <p>Where luxury is intentional.</p>
              <p>Where every gem is chosen with purpose.</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;